import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactSelect from 'react-select';
import api from '~/services/api';
import { NcmContainer } from './styles';
import { FieldValues, Controller, Control } from 'react-hook-form';

/**
 * Option → tipo usado internamente pelo react-select
 * value: código do NCM
 * label: texto exibido no select
 * cod_tipo_item: metadado adicional (se necessário futuramente)
 */
type Option = {
  label: string;
  value: number | string;
  cod_tipo_item?: number;
};

type Ncm = {
  cod_ncm: number;
  descricao: string;
  flg_inativo?: boolean;
  cod_tipo_item?: number;
};

export type BuscaNcmRef = {
  /** Limpa o NCM selecionado (reset visual) */
  handleClearNcm: () => void;
};

type BuscaNcmProps = {
  /** Nome do campo */
  name: string;

  /** Control do react-hook-form */
  control: Control<FieldValues>;

  /** Placeholder do select */
  placeholder?: string;

  /** Label do campo */
  label?: string;

  /** Regra de busca (>=3 chars em modo update) */
  isUpdate: boolean;

  /** Flag de inicialização (usada p/ validação visual) */
  init: boolean;

  /** Estado de erro externo */
  invalid?: boolean;

  /** Desabilitado */
  isDisabled?: boolean;

  /** Id base para acessibilidade */
  idBase?: string;

  /** Conteúdo adicional */
  children?: ReactNode;

  /** Target do menu (z-index fix) */
  menuPortalTarget?: HTMLElement | null;
};

/**
 * BuscaNcm — select de NCM com filtro por digitação (>=3 chars), A11y e estilo "Atlaskit-like"
 * @example
 * <BuscaNcm
 *   value={getValues('cod_ncm')?.value ?? null}
 *   onChange={(opt) => setValue('cod_ncm', opt ?? '')}
 *   isUpdate={false}
 *   init={formSubmitted}
 *   invalid={!!errors?.cod_ncm}
 * />
 */
export const BuscaNcm = forwardRef<BuscaNcmRef, BuscaNcmProps>((props, ref) => {
  const {
    name,
    control,
    placeholder = 'Digite para buscar o NCM',
    label = 'NCM *',
    isUpdate,
    init,
    invalid = false,
    isDisabled = false,
    idBase,
    children,
    menuPortalTarget,
  } = props;
  const [allOptions, setAllOptions] = useState<Option[]>([]);
  const [activeOptions, setActiveOptions] = useState<Option[]>([]);
  const [filtered, setFiltered] = useState<Option[]>([]);
  const [selected, setSelected] = useState<Option | null>(null);
  const [inicializado, setInicializado] = useState<boolean>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const uidRef = useRef<string>(
    idBase || `buscanmc-${Math.random().toString(36).slice(2, 8)}`,
  );
  const inputId = `${uidRef.current}-input`;
  const labelId = `${uidRef.current}-label`;
  const errorId = `${uidRef.current}-error`;

  useEffect(() => setInicializado(init), [init]);
  useEffect(() => setIsInvalid(invalid), [invalid]);

  useImperativeHandle(
    ref,
    () => ({
      handleClearNcm: () => {
        setSelected(null);
      },
    }),
    [],
  );

  /* ===================== API ===================== */
  const getNcms = useCallback(async () => {
    const res = await api.get('/produto/ncms');
    const { data } = res.data as { data: Ncm[] };

    const ativos: Option[] = data.flatMap((ncm) => {
      if (ncm.flg_inativo !== true) {
        return [
          {
            value: ncm.cod_ncm,
            label: `${ncm.cod_ncm} - ${ncm.descricao}`,
            cod_tipo_item: ncm.cod_tipo_item,
          },
        ];
      }
      return [];
    });

    const todos: Option[] = data.map((ncm) => ({
      value: ncm.cod_ncm,
      label: `${ncm.cod_ncm} - ${ncm.descricao}`,
      cod_tipo_item: ncm.cod_tipo_item,
    }));

    setActiveOptions(ativos);
    setAllOptions(todos);
    setFiltered([]);
  }, []);

  useEffect(() => {
    getNcms();
  }, [getNcms]);

  const selectedFromValue = (val: any): Option | null => {
    if (
      val === null ||
      val === undefined ||
      val === '' ||
      (typeof val === 'object' && (!val.value || !val.label))
    ) {
      return null;
    }

    if (typeof val === 'object') {
      return val as Option;
    }

    return allOptions.find((o) => String(o.value) === String(val)) ?? null;
  };

  /* ===================== Filtro ===================== */
  const handleFilter = useCallback(
    (text: string) => {
      const t = (text || '').trim();

      if (isUpdate) {
        if (t.length < 3) {
          setFiltered([]);
          return t;
        }
      } else if (t === '' || t.length < 3) {
        setFiltered([]);
        return t;
      }

      const needle = t.toUpperCase();
      const result = activeOptions.filter((opt) =>
        opt.label.toUpperCase().includes(needle),
      );

      setFiltered(result);
      return t;
    },
    [isUpdate, activeOptions],
  );

  const handleChange = useCallback((opt: Option | null) => {
    setSelected(opt);
  }, []);

  /* ===================== Styles ===================== */
  const selectStyles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        minHeight: 38,
        backgroundColor: isDisabled
          ? 'var(--ds-background-disabled,#FAFBFC)'
          : 'var(--ds-background-subtleBorderedNeutral-resting,#FAFBFC)',
        border: '2px solid var(--ds-border-neutral, #DFE1E6)',
        boxShadow: state.isFocused
          ? '0 0 0 3px rgba(76,154,255,.3)'
          : isInvalid && inicializado
          ? 'inset 0 -2px 0 0 #cf1919'
          : 'none',
        borderRadius: 3,
        transition:
          'background-color 200ms ease-in-out, border-color 200ms ease-in-out, box-shadow 200ms ease-in-out',
        '&:hover': {
          backgroundColor: isDisabled
            ? 'var(--ds-background-disabled,#FAFBFC)'
            : 'var(--ds-background-default, #EBECF0)',
          borderColor: 'var(--ds-border-neutral, #EBECF0)',
        },
        ...(state.isFocused &&
          !isDisabled && {
            backgroundColor: 'var(--ds-background-default, #ffffff)',
            borderColor: 'var(--ds-border-focus, #4C9AFF)',
            outline: 0,
          }),
        ...(isDisabled && {
          cursor: 'not-allowed',
          opacity: 0.7,
          pointerEvents: 'all',
        }),
      }),
      valueContainer: (base: any) => ({
        ...base,
        padding: '2px 8px',
        fontSize: '0.875rem',
        cursor: isDisabled ? 'not-allowed' : 'text',
        pointerEvents: isDisabled ? 'none' : 'auto',
      }),
      input: (base: any) => ({
        ...base,
        margin: 0,
        padding: 0,
        cursor: isDisabled ? 'not-allowed' : 'text',
        pointerEvents: isDisabled ? 'none' : 'auto',
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto',
      }),
      placeholder: (base: any) => ({
        ...base,
        color: 'var(--ds-text-lowEmphasis,#7A869A)',
        cursor: isDisabled ? 'not-allowed' : 'text',
      }),
      singleValue: (base: any) => ({
        ...base,
        color: 'var(--ds-text-highEmphasis,#172B4D)',
        cursor: isDisabled ? 'not-allowed' : 'text',
      }),
      menu: (base: any) => ({ ...base, zIndex: 5 }),
      menuList: (base: any) => ({
        ...base,
        maxHeight: 300,
        '::-webkit-scrollbar': { width: 8 },
        '::-webkit-scrollbar-track': { background: '#f0f0f0' },
        '::-webkit-scrollbar-thumb': {
          background: '#cecece',
          borderRadius: '0px 4px 4px 0px',
        },
        '::-webkit-scrollbar-thumb:hover': { background: '#b0b0b0' },
      }),
      option: (base: any, state: any) => ({
        ...base,
        minHeight: 31,
        lineHeight: '31px',
        padding: '0 15px',
        fontSize: 'inherit',
        color: state.isSelected
          ? 'var(--ds-text-selected,#0052cc)'
          : 'var(--ds-text-highEmphasis,#172b4d)',
        backgroundColor:
          state.isFocused || state.isSelected
            ? 'var(--ds-background-selected-hover,#deebff)'
            : 'transparent',
        boxShadow:
          state.isFocused || state.isSelected
            ? 'inset 2px 0px 0px var(--ds-text-selected,#0052cc)'
            : 'none',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      }),
    }),
    [isDisabled, isInvalid, inicializado],
  );

  return (
    <NcmContainer>
      <div className="form-group">
        <label id={labelId} htmlFor={inputId} className="form-label">
          {label}
        </label>

        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange, ref: reef }, fieldState }) => (
            <ReactSelect
              ref={reef}
              inputId={inputId}
              aria-labelledby={labelId}
              aria-describedby={fieldState.error ? errorId : undefined}
              aria-invalid={!!fieldState.error}
              classNamePrefix="rs"
              placeholder={placeholder}
              options={filtered}
              value={selectedFromValue(value)}
              onChange={(opt) => {
                handleChange(opt as Option | null);
                onChange(opt);
              }}
              onInputChange={(val) => handleFilter(val)}
              isDisabled={isDisabled}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
              styles={selectStyles}
              menuPortalTarget={menuPortalTarget}
            />
          )}
        />

        {children}
      </div>
    </NcmContainer>
  );
});

BuscaNcm.displayName = 'BuscaNcm';
